

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
    Switch,
    FormControlLabel
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewTravelOrderItemModal = ({
    isOpen,
    onClose,
    onSave,
    travelOrderId = null
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        travel_order_id: travelOrderId || '',
        trip_component_id: '',
        merchant_allocation_id: '',
        merchant_id: '',
        pax_count: '',
        service_start: '',
        service_end: '',
        unit_price: '',
        fulfillment_status: 'Pending',
        merchant_confirmation_no: '',
        merchant_acknowledgement: false,
        merchant_order_sent: false
    });

    const [formErrors, setFormErrors] = useState({});
    const [travelOrders, setTravelOrders] = useState([]);
    const [tripComponents, setTripComponents] = useState([]);
    const [merchantAllocations, setMerchantAllocations] = useState([]);
    const [merchants, setMerchants] = useState([]);
    const [loadingTravelOrders, setLoadingTravelOrders] = useState(false);
    const [loadingComponents, setLoadingComponents] = useState(false);
    const [loadingAllocations, setLoadingAllocations] = useState(false);
    const [loadingMerchants, setLoadingMerchants] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            if (!travelOrderId) {
                fetchTravelOrders();
            }
            fetchTripComponents();
            fetchMerchantAllocations();
            fetchMerchants();
        }
    }, [isOpen, travelOrderId]);

    const fetchTravelOrders = async () => {
        setLoadingTravelOrders(true);
        try {
            const response = await axiosInstance.get('/travel-orders/');
            setTravelOrders(response.data || []);
        } catch (error) {
            console.error('Error fetching travel orders:', error);
        } finally {
            setLoadingTravelOrders(false);
        }
    };

    const fetchTripComponents = async () => {
        setLoadingComponents(true);
        try {
            const response = await axiosInstance.get('/trip-components/');
            setTripComponents(response.data || []);
        } catch (error) {
            console.error('Error fetching Trip Logistics:', error);
        } finally {
            setLoadingComponents(false);
        }
    };

    const fetchMerchantAllocations = async () => {
        setLoadingAllocations(true);
        try {
            const response = await axiosInstance.get('/merchant-allocations/');
            setMerchantAllocations(response.data || []);
        } catch (error) {
            console.error('Error fetching merchant allocations:', error);
        } finally {
            setLoadingAllocations(false);
        }
    };

    const fetchMerchants = async () => {
        setLoadingMerchants(true);
        try {
            const response = await axiosInstance.get('/accounts/?dropdown=true');
            setMerchants(response.data || []);
        } catch (error) {
            console.error('Error fetching merchants:', error);
        } finally {
            setLoadingMerchants(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Item Name is required';
        if (!formData.travel_order_id) errors.travel_order_id = 'Travel Order is required';
        if (!formData.trip_component_id) errors.trip_component_id = 'Trip Logistics is required';
        if (!formData.pax_count) errors.pax_count = 'Pax Count is required';
        if (!formData.unit_price) errors.unit_price = 'Unit Price is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                travel_order_id: parseInt(formData.travel_order_id),
                trip_component_id: parseInt(formData.trip_component_id),
                merchant_allocation_id: formData.merchant_allocation_id ? parseInt(formData.merchant_allocation_id) : null,
                merchant_id: formData.merchant_id ? parseInt(formData.merchant_id) : null,
                pax_count: parseInt(formData.pax_count),
                unit_price: parseFloat(formData.unit_price),
                service_start: formData.service_start ? new Date(formData.service_start).toISOString() : null,
                service_end: formData.service_end ? new Date(formData.service_end).toISOString() : null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '',
                travel_order_id: travelOrderId || '',
                trip_component_id: '',
                merchant_allocation_id: '',
                merchant_id: '',
                pax_count: '',
                service_start: '',
                service_end: '',
                unit_price: '',
                fulfillment_status: 'Pending',
                merchant_confirmation_no: '',
                merchant_acknowledgement: false,
                merchant_order_sent: false
            });
        } catch (error) {
            console.error('Error saving Travel Order Item:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Travel Order Item',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '900px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Travel Order Item</MDTypography>
                        <IconButton onClick={onClose} variant="outlined" margin="normal" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Item Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                {!travelOrderId && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.travel_order_id} required>
                                            <InputLabel>Travel Order</InputLabel>
                                            <Select
                                                name="travel_order_id"
                                                value={formData.travel_order_id}
                                                onChange={handleInputChange}
                                                label="Travel Order"

                                                disabled={loadingTravelOrders}
                                            >
                                                <MenuItem value=""><em>Select a Travel Order</em></MenuItem>
                                                {travelOrders.map(to => (
                                                    <MenuItem key={to.id} value={to.id}>{to.name}</MenuItem>
                                                ))}
                                            </Select>
                                            {formErrors.travel_order_id && <MDTypography variant="caption" color="error">{formErrors.travel_order_id}</MDTypography>}
                                        </FormControl>
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.trip_component_id} required>
                                        <InputLabel>Trip Logistics</InputLabel>
                                        <Select
                                            name="trip_component_id"
                                            value={formData.trip_component_id}
                                            onChange={handleInputChange}
                                            label="Trip Logistics"

                                            disabled={loadingComponents}
                                        >
                                            <MenuItem value=""><em>Select a Trip Logistics</em></MenuItem>
                                            {tripComponents.map(tc => (
                                                <MenuItem key={tc.id} value={tc.id}>{tc.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.trip_component_id && <MDTypography variant="caption" color="error">{formErrors.trip_component_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Merchant Allocation</InputLabel>
                                        <Select
                                            name="merchant_allocation_id"
                                            value={formData.merchant_allocation_id}
                                            onChange={handleInputChange}
                                            label="Merchant Allocation"

                                            disabled={loadingAllocations}
                                        >
                                            <MenuItem value=""><em>Select a Merchant Allocation</em></MenuItem>
                                            {merchantAllocations.map(ma => (
                                                <MenuItem key={ma.id} value={ma.id}>{ma.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Merchant</InputLabel>
                                        <Select
                                            name="merchant_id"
                                            value={formData.merchant_id}
                                            onChange={handleInputChange}
                                            label="Merchant"

                                            disabled={loadingMerchants}
                                        >
                                            <MenuItem value=""><em>Select a Merchant</em></MenuItem>
                                            {merchants.map(m => (
                                                <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Pax Count"
                                        name="pax_count"
                                        type="number"
                                        value={formData.pax_count}
                                        onChange={handleInputChange}
                                        error={!!formErrors.pax_count}
                                        helperText={formErrors.pax_count}
                                        required
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Unit Price"
                                        name="unit_price"
                                        type="number"
                                        value={formData.unit_price}
                                        onChange={handleInputChange}
                                        error={!!formErrors.unit_price}
                                        helperText={formErrors.unit_price}
                                        required
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Service Start"
                                        name="service_start"
                                        type="datetime-local"
                                        value={formData.service_start}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Service End"
                                        name="service_end"
                                        type="datetime-local"
                                        value={formData.service_end}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Fulfillment Status</InputLabel>
                                        <Select
                                            name="fulfillment_status"
                                            value={formData.fulfillment_status}
                                            onChange={handleInputChange}
                                            label="Fulfillment Status"

                                        >
                                            <MenuItem value="Pending">Pending</MenuItem>
                                            <MenuItem value="Allocated">Allocated</MenuItem>
                                            <MenuItem value="Confirmed">Confirmed</MenuItem>
                                            <MenuItem value="Waitlisted">Waitlisted</MenuItem>
                                            <MenuItem value="Cancelled">Cancelled</MenuItem>
                                            <MenuItem value="Split">Split</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Merchant Confirmation No"
                                        name="merchant_confirmation_no"
                                        value={formData.merchant_confirmation_no}
                                        onChange={handleInputChange}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <MDBox mt={1}>
                                        <FormControlLabel
                                            control={<Switch checked={formData.merchant_acknowledgement} name="merchant_acknowledgement" onChange={handleInputChange} />}
                                            label="Merchant Acknowledgement"
                                            sx={{ mt: 2 }}
                                        />
                                    </MDBox>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <MDBox mt={1}>
                                        <FormControlLabel
                                            control={<Switch checked={formData.merchant_order_sent} name="merchant_order_sent" onChange={handleInputChange} />}
                                            label="Order Sent to Merchant"
                                            sx={{ mt: 2 }}
                                        />
                                    </MDBox>
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Travel Order Item'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewTravelOrderItemModal;

